import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  getBanner,
  putBanner,
  servicesApi,
  testimonialsApi,
  galleryApi,
  addonsApi,
} from "@/lib/content";
import { Loader2, Save, Plus, Trash2, Pencil, X } from "lucide-react";

// IST helpers for the offer countdown deadline field
const toIstInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() + 330 * 60000).toISOString().slice(0, 16);
};
const fromIstInput = (v) => (v ? `${v}:00+05:30` : null);

// ---------- Banner ----------
function BannerEditor() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getBanner().then(setB).catch(() => setB({ active: true, label: "", headline: "", cta_label: "" })); }, []);

  const update = (k) => (v) => setB((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setSaving(true);
    try { await putBanner(b); toast.success("Banner saved"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!b) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="rounded-2xl bg-white hairline shadow-soft p-6 md:p-7" data-testid="content-banner">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg text-ink">Promo banner</h3>
        <div className="flex items-center gap-2">
          <Label htmlFor="banner-active" className="text-xs font-bold">Active</Label>
          <Switch id="banner-active" checked={!!b.active} onCheckedChange={update("active")} data-testid="banner-active-switch" />
        </div>
      </div>
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold text-ink">Label (pill)</Label>
          <Input className="mt-1.5 rounded-xl" value={b.label || ""} onChange={update("label")} data-testid="banner-label" />
        </div>
        <div>
          <Label className="text-xs font-bold text-ink">CTA label</Label>
          <Input className="mt-1.5 rounded-xl" value={b.cta_label || ""} onChange={update("cta_label")} data-testid="banner-cta" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold text-ink">Headline</Label>
          <Input className="mt-1.5 rounded-xl" value={b.headline || ""} onChange={update("headline")} data-testid="banner-headline" />
        </div>
        <div>
          <Label className="text-xs font-bold text-ink">Offer countdown ends (IST)</Label>
          <Input type="datetime-local" className="mt-1.5 rounded-xl" value={toIstInput(b.end_at)}
                 onChange={(e) => update("end_at")(fromIstInput(e.target.value))} data-testid="banner-end-at" />
          <p className="mt-1 text-[11px] text-muted-ink">The banner hides itself once this time passes.</p>
        </div>
      </div>
      <Button onClick={save} disabled={saving}
              className="mt-5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="banner-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save banner
      </Button>
    </div>
  );
}

// ---------- Generic list editor ----------
function ListEditor({
  apiClient,
  kind,
  fields,
  emptyItem,
  render,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, ...fields }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await apiClient.list()); } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startCreate = () => setEditing({ ...emptyItem });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.id;
      if (editing.id) { await apiClient.update(editing.id, body); toast.success(`${kind} updated`); }
      else { await apiClient.create(body); toast.success(`${kind} added`); }
      setEditing(null);
      await load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${kind.toLowerCase()}?`)) return;
    try { await apiClient.remove(id); toast.success(`${kind} deleted`); await load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="rounded-2xl bg-white hairline shadow-soft p-5 md:p-6" data-testid={`content-${kind.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-ink">{kind}</h3>
        <Button onClick={startCreate}
                className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                data-testid={`${kind.toLowerCase()}-add`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="p-10 grid place-items-center text-muted-ink"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.length === 0 && (
            <div className="p-8 rounded-xl bg-slate-50 text-center text-muted-ink text-sm">No items yet.</div>
          )}
          {items.map((it) => (
            <div key={it.id} className="rounded-xl hairline bg-white p-4 flex items-start gap-3 flex-wrap"
                 data-testid={`${kind.toLowerCase()}-row-${it.id}`}>
              <div className="flex-1 min-w-0">{render(it)}</div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => startEdit(it)}
                        className="h-8 w-8 rounded-lg grid place-items-center bg-slate-100 hover:bg-slate-200" title="Edit"
                        data-testid={`${kind.toLowerCase()}-edit-${it.id}`}>
                  <Pencil className="h-4 w-4 text-ink" />
                </button>
                <button onClick={() => remove(it.id)}
                        className="h-8 w-8 rounded-lg grid place-items-center bg-red-50 hover:bg-red-100 text-red-600" title="Delete"
                        data-testid={`${kind.toLowerCase()}-delete-${it.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={cancel}>
          <div className="relative max-w-lg w-full rounded-2xl bg-white shadow-xl p-6" onClick={(e) => e.stopPropagation()}
               data-testid={`${kind.toLowerCase()}-editor-modal`}>
            <button onClick={cancel} className="absolute top-3 right-3 h-8 w-8 rounded-lg hover:bg-slate-100 grid place-items-center">
              <X className="h-4 w-4" />
            </button>
            <h4 className="font-display font-bold text-lg text-ink">
              {editing.id ? `Edit ${kind.toLowerCase()}` : `Add ${kind.toLowerCase()}`}
            </h4>
            <div className="mt-4 grid grid-cols-1 gap-3 max-h-[60vh] overflow-auto pr-1">
              {fields.map((f) => (
                <div key={f.key} className={f.full ? "" : ""}>
                  <Label className="text-xs font-bold text-ink">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      rows={3}
                      className="mt-1.5 rounded-xl"
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      data-testid={`${kind.toLowerCase()}-field-${f.key}`}
                    />
                  ) : f.type === "number" ? (
                    <Input
                      type="number"
                      className="mt-1.5 rounded-xl"
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })}
                      data-testid={`${kind.toLowerCase()}-field-${f.key}`}
                    />
                  ) : f.type === "bool" ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <Switch
                        checked={!!editing[f.key]}
                        onCheckedChange={(v) => setEditing({ ...editing, [f.key]: v })}
                        data-testid={`${kind.toLowerCase()}-field-${f.key}`}
                      />
                      <span className="text-sm text-ink-2">{editing[f.key] ? "Yes" : "No"}</span>
                    </div>
                  ) : f.type === "select" ? (
                    <Select
                      value={editing[f.key] ?? ""}
                      onValueChange={(v) => setEditing({ ...editing, [f.key]: v })}
                    >
                      <SelectTrigger className="mt-1.5 rounded-xl" data-testid={`${kind.toLowerCase()}-field-${f.key}`}>
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "csv" ? (
                    <Input
                      className="mt-1.5 rounded-xl"
                      value={Array.isArray(editing[f.key]) ? editing[f.key].join(", ") : (editing[f.key] || "")}
                      onChange={(e) => setEditing({
                        ...editing,
                        [f.key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      })}
                      placeholder="Feature 1, Feature 2, Feature 3"
                      data-testid={`${kind.toLowerCase()}-field-${f.key}`}
                    />
                  ) : (
                    <Input
                      className="mt-1.5 rounded-xl"
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      data-testid={`${kind.toLowerCase()}-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" onClick={cancel} className="rounded-full">Cancel</Button>
              <Button onClick={save} disabled={saving}
                      className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                      data-testid={`${kind.toLowerCase()}-save`}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Field configs
const SERVICE_FIELDS = [
  { key: "title", label: "Title" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "icon", label: "Lucide icon (e.g., Paintbrush, Home, Droplets, RefreshCw, Sparkles)" },
  { key: "order", label: "Order", type: "number" },
];
const SERVICE_EMPTY = { title: "", description: "", icon: "Paintbrush", order: 99 };

const TESTIMONIAL_FIELDS = [
  { key: "name", label: "Name" },
  { key: "area", label: "Area / locality" },
  { key: "body", label: "Review text", type: "textarea" },
  { key: "rating", label: "Rating (1–5)", type: "number" },
  { key: "img", label: "Image URL (optional)" },
  { key: "order", label: "Order", type: "number" },
];
const TESTIMONIAL_EMPTY = { name: "", area: "", body: "", rating: 5, img: "", order: 99 };

const GALLERY_FIELDS = [
  { key: "title", label: "Title (e.g., 2 BHK · Whitefield)" },
  { key: "tag", label: "Tag (e.g., Interior repaint)" },
  { key: "before_url", label: "Before image URL" },
  { key: "after_url", label: "After image URL" },
  { key: "order", label: "Order", type: "number" },
];
const GALLERY_EMPTY = { title: "", tag: "", before_url: "", after_url: "", order: 99 };

const ADDON_FIELDS = [
  { key: "label", label: "Label" },
  { key: "price", label: "Price (₹)", type: "number" },
  { key: "per", label: "Priced per", type: "select", options: ["flat", "door"] },
  { key: "icon", label: "Lucide icon (Bath, DoorClosed, PaintBucket, Sparkles)" },
  { key: "active", label: "Active (visible in calculator)", type: "bool" },
  { key: "order", label: "Order", type: "number" },
];
const ADDON_EMPTY = { label: "", price: 0, per: "flat", icon: "Sparkles", active: true, order: 99 };

export default function AdminContent() {
  const navigate = useNavigate();
  useEffect(() => {
    getBanner().catch((e) => {
      if (e?.response?.status === 401) navigate("/login", { replace: true });
    });
  }, [navigate]);

  return (
    <div data-testid="admin-content">
      <div>
        <h2 className="font-display font-bold text-2xl text-ink">Content</h2>
        <p className="text-sm text-muted-ink mt-1">
          Manage the promo banner, services, calculator add-ons, testimonials and gallery that visitors see.
        </p>
      </div>

      <Tabs defaultValue="banner" className="mt-5">
        <TabsList className="rounded-full bg-white hairline p-1 h-11 w-full md:w-fit overflow-x-auto">
          {["banner", "services", "add-ons", "testimonials", "gallery"].map((k) => (
            <TabsTrigger
              key={k}
              value={k}
              className="rounded-full px-4 data-[state=active]:bg-ink data-[state=active]:text-white font-bold capitalize"
              data-testid={`content-tab-${k}`}
            >
              {k}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="banner" className="mt-5"><BannerEditor /></TabsContent>
        <TabsContent value="services" className="mt-5">
          <ListEditor
            apiClient={servicesApi}
            kind="Services"
            fields={SERVICE_FIELDS}
            emptyItem={SERVICE_EMPTY}
            render={(it) => (
              <>
                <div className="font-bold text-ink">{it.title}</div>
                <div className="text-sm text-ink-2 mt-0.5 line-clamp-2">{it.description}</div>
                <div className="mt-1 text-[11px] text-muted-ink font-mono">icon: {it.icon} · order {it.order}</div>
              </>
            )}
          />
        </TabsContent>
        <TabsContent value="add-ons" className="mt-5">
          <ListEditor
            apiClient={addonsApi}
            kind="Add-ons"
            fields={ADDON_FIELDS}
            emptyItem={ADDON_EMPTY}
            render={(it) => (
              <>
                <div className="font-bold text-ink">{it.label}</div>
                <div className="mt-1 text-[11px] text-muted-ink font-mono">
                  ₹{new Intl.NumberFormat("en-IN").format(it.price || 0)}{" "}
                  {it.per === "door" ? "/ door" : "flat"} · {it.active ? "active" : "hidden"} · order {it.order}
                </div>
              </>
            )}
          />
        </TabsContent>
        <TabsContent value="testimonials" className="mt-5">
          <ListEditor
            apiClient={testimonialsApi}
            kind="Testimonials"
            fields={TESTIMONIAL_FIELDS}
            emptyItem={TESTIMONIAL_EMPTY}
            render={(it) => (
              <>
                <div className="flex items-center gap-2">
                  {it.img ? <img src={it.img} alt="" className="h-8 w-8 rounded-full object-cover" /> : null}
                  <div>
                    <div className="font-bold text-ink">{it.name} <span className="text-muted-ink font-normal">· {it.area}</span></div>
                    <div className="text-xs text-muted-ink">{"★".repeat(it.rating || 5)}</div>
                  </div>
                </div>
                <div className="text-sm text-ink-2 mt-1 line-clamp-2">“{it.body}”</div>
              </>
            )}
          />
        </TabsContent>
        <TabsContent value="gallery" className="mt-5">
          <ListEditor
            apiClient={galleryApi}
            kind="Gallery"
            fields={GALLERY_FIELDS}
            emptyItem={GALLERY_EMPTY}
            render={(it) => (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-1">
                  <img src={it.before_url} alt="" className="h-14 w-14 object-cover rounded-lg" />
                  <img src={it.after_url} alt="" className="h-14 w-14 object-cover rounded-lg" />
                </div>
                <div>
                  <div className="font-bold text-ink">{it.title}</div>
                  <div className="text-xs text-muted-ink">{it.tag}</div>
                </div>
              </div>
            )}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
